import React, {isValidElement, useMemo, useRef} from 'react';
import cn from 'classnames';

import {EEventType} from 'coreClasses/events/IEvents';
import {useLocalesMgr, useProduct} from 'coreHooks';
import {useCategory} from 'coreHooks/categories';
import {appendParamsToURL, getIsInternalUrl} from 'coreUtils/url';

import Icon from 'brandComponents/Icon';
import Link from 'brandComponents/Link';
import {usePromotionViewEvent} from 'brandHooks/usePromotionViewEvent';
import {useTrackPromotionEvent} from 'brandHooks/useTrackPromotionEvent';

import CTATargetCategoryFactory from './Subcomponents/CTATargetCategoryFactory';
import {ECTALinkTarget, ICTAProps, TLinkAttributes} from './ICTA';

import styles from './CTA.module.scss';

const CTA = ({
	longTargetUrl,
	targetCategory,
	targetProduct,
	title,
	icon,
	targetElement,
	linkTarget,
	targetPage,
	queryParameters,
	showOnDevice,
	media,
	image,
	variant,
	backgroundColor,
	color,
	renderTitle,
	style,
	classNames,
	...props
}: ICTAProps) => {
	const localesMgr = useLocalesMgr();
	const category = useCategory(targetCategory);
	const productUrl = useProduct(targetProduct?.items?.[0]?.id)?.getProductUrl();
	const elementRef = useRef(null);
	const {trackPromotionEvent} = useTrackPromotionEvent();

	const targetUrl = useMemo(() => {
		const raw = longTargetUrl || props?.targetUrl;

		if (targetPage?.slug) {
			return localesMgr.updateLinkWithLocale(targetPage.slug);
		}

		if (targetPage?.props?.slug) {
			return localesMgr.updateLinkWithLocale(targetPage.props.slug);
		}

		if (raw) {
			return getIsInternalUrl(raw) ? localesMgr.updateLinkWithLocale(raw) : raw;
		}

		if (category) {
			return category?.getUrl();
		}

		if (!targetCategory && productUrl) {
			return productUrl;
		}

		return undefined;
	}, [longTargetUrl, props?.targetUrl, targetPage, category, targetCategory, productUrl]);

	const imageWithThreshold = useMemo(() => {
		if (isValidElement(image) && typeof props.threshold === 'number') {
			return React.cloneElement(image as React.ReactElement, {threshold: props.threshold});
		}

		return image;
	}, [image, props.threshold]);

	const isInternalUrl = getIsInternalUrl(targetUrl);

	usePromotionViewEvent(elementRef, targetUrl?.toString());

	if (targetElement) {
		return <CTATargetCategoryFactory targetElement={targetElement} title={title} targetUrl={targetUrl} />;
	}

	const resolvedImage = media || imageWithThreshold;

	const linkAttrs: TLinkAttributes = {
		title: title || icon,
	};
	let AnchorTag: string | typeof Link = Link;
	let hasLink = !!targetUrl;

	if (!isInternalUrl) {
		AnchorTag = 'a';
		linkAttrs.rel = 'noopener noreferrer';
		linkAttrs.target = '_blank';
		linkAttrs.href = targetUrl;
	} else {
		linkAttrs.to = targetUrl;
	}

	if (linkTarget) {
		linkAttrs.target = ECTALinkTarget[linkTarget];
	}

	if (queryParameters) {
		linkAttrs.to = appendParamsToURL(linkAttrs.to, queryParameters);

		linkAttrs.onClick = () => {
			trackPromotionEvent(linkAttrs.to, EEventType.SELECT_BRAND_PROMOTION);
		};
	}

	if (icon && hasLink) {
		linkAttrs.className = cn('cta cta--link', styles['cta__link'], styles['cta']);

		if (isValidElement(resolvedImage) && variant === 'over-image') {
			return (
				<div
					ref={elementRef}
					className={cn('cta-wrapper', styles['cta-wrapper'], {
						[styles[`cta__${variant}__wrapper`]]: variant,
						[styles[`cta-wrapper-show-on-${showOnDevice}`]]: showOnDevice,
					})}
				>
					<AnchorTag {...linkAttrs}>
						<span className={cn('cta-image', {[styles[`cta__${variant}__wrapper--image`]]: variant})}>{resolvedImage}</span>

						<span
							className={cn('cta-text_wrapper', {
								[styles[`cta__${variant}__wrapper--text--wrapper`]]: variant,
								[styles[`cta__bg--${backgroundColor}`]]: backgroundColor,
								[styles[`cta__color--${color}`]]: color,
							})}
						>
							<Icon
								classNames={cn('cta-icon', {
									[styles[`cta__${variant}__wrapper--text--wrapper--icon`]]: variant,
								})}
								name={icon}
							/>
							<span
								className={cn('cta-title', {
									[styles[`cta__${variant}__wrapper--text--wrapper--title`]]: variant,
								})}
							>
								{title}
							</span>
						</span>
					</AnchorTag>
				</div>
			);
		}

		return (
			<div
				ref={elementRef}
				className={cn('cta-wrapper', styles['cta-wrapper'], styles['cta-wrapper--icon'], {
					[styles[`cta__${variant}--icon__wrapper`]]: variant,
					[styles[`cta-wrapper-show-on-${showOnDevice}`]]: showOnDevice,
				})}
			>
				<AnchorTag {...linkAttrs}>
					<Icon
						classNames={cn('cta-icon', {
							[styles[`cta__${variant}--icon`]]: variant,
						})}
						name={icon}
					/>
					<span className={cn('cta-title', {[styles[`cta__${variant}`]]: variant})}>{title}</span>
				</AnchorTag>
			</div>
		);
	}

	if (!isValidElement(resolvedImage) && hasLink && !variant) {
		linkAttrs.className = cn('cta cta--link', styles['cta']);

		return (
			<AnchorTag ref={elementRef} {...linkAttrs}>
				{linkAttrs.title}
			</AnchorTag>
		);
	}

	if (!isValidElement(resolvedImage) && !hasLink) {
		return (
			<span className={cn('cta cta--text', styles['cta'])}>
				{linkAttrs.title}
			</span>
		);
	}

	if (isValidElement(resolvedImage) && renderTitle) {
		if (hasLink) {
			return (
				<div
					ref={elementRef}
					className={cn('cta-wrapper', styles['cta-wrapper'], {
						[styles[`cta-wrapper-show-on-${showOnDevice}`]]: showOnDevice,
					})}
				>
					<AnchorTag {...linkAttrs}>
						<div className="cta-image">
							{resolvedImage}
						</div>
						<div className={cn('cta-title', {[styles[`cta__${variant}`]]: variant})}>
							{title}
						</div>
					</AnchorTag>
				</div>
			);
		} else {
			return (
				<div
					className={cn('cta-wrapper', styles['cta-wrapper'], {
						[styles[`cta-wrapper-show-on-${showOnDevice}`]]: showOnDevice,
					})}
				>
					<div className="cta-image">
						{resolvedImage}
					</div>
					<div className="cta-title">
						{title}
					</div>
				</div>
			);
		}
	}

	if (isValidElement(resolvedImage) && hasLink) {
		linkAttrs.className = cn('cta cta--image', styles['cta']);

		return (
			<div
				ref={elementRef}
				className={cn('cta-wrapper', style, classNames, styles['cta-wrapper'], {
					[styles[`cta-wrapper-show-on-${showOnDevice}`]]: showOnDevice,
				})}
			>
				<AnchorTag {...linkAttrs}>
					{resolvedImage}
				</AnchorTag>
			</div>
		);
	}

	if (variant && hasLink) {
		linkAttrs.className = cn('cta cta--link', styles['cta'], {
			[styles[`cta__${variant}`]]: variant,
			[`cta__${variant}`]: variant,
		});

		return (
			<div
				ref={elementRef}
				className={cn('cta-wrapper', styles['cta-wrapper'], {
					[styles[`cta-wrapper-show-on-${showOnDevice}`]]: showOnDevice,
				})}
			>
				<AnchorTag {...linkAttrs}>
					<span>{linkAttrs.title}</span>
				</AnchorTag>
			</div>
		);
	}

	return (
		isValidElement(resolvedImage) && (
			<div className={cn('cta-wrapper', style, classNames)}>
				{resolvedImage}
			</div>
		)
	);
};

export default CTA;
